import { z } from 'zod';
/** Zod schema for a single doer step (title + description). */
export const doerStepSchema = z.object({
    title: z.string().min(1),
    description: z.string().min(1),
});
/**
 * How many steps a `*_page_steps` column may hold.
 *
 * The founder page lays them out in a fixed 4-up grid, so this is a layout
 * constraint the API enforces. Exported so the CMS editor can stop an author at
 * the same number rather than letting them write a fifth step the API will 400.
 */
export const MAX_DOER_STEPS = 4;
/** Zod schema for the doer_steps JSONB column (array of 1-4 steps). */
export const doerStepsSchema = z.array(doerStepSchema).min(1).max(MAX_DOER_STEPS);
/**
 * Normalise a `*_page_steps` JSONB payload into usable steps, dropping anything
 * malformed rather than throwing.
 *
 * Lenient where `doerStepsSchema` is strict, because the two answer different
 * questions. The Zod schema guards the API write path — a caller sending a
 * broken step should get a 400. This runs on the read path, where the value is
 * already in the database and the only useful behaviour is to render whatever is
 * salvageable. It also accepts a JSON-encoded string, which some legacy rows
 * carry.
 *
 * Shared so the founder-facing card, the CMS preview and `getPlaybookReadiness`
 * all agree on what "this playbook has steps" means.
 */
export function normalizeDoerSteps(rawSteps) {
    let parsed = rawSteps;
    if (typeof rawSteps === 'string') {
        try {
            parsed = JSON.parse(rawSteps);
        }
        catch {
            return [];
        }
    }
    if (!Array.isArray(parsed))
        return [];
    return parsed.flatMap((step) => {
        const result = doerStepSchema.safeParse(step);
        if (!result.success)
            return [];
        const title = result.data.title.trim();
        const description = result.data.description.trim();
        // `min(1)` passes on whitespace-only strings; the trim is what actually
        // rejects them.
        if (!title || !description)
            return [];
        return [{ title, description }];
    });
}
//# sourceMappingURL=doer-steps.js.map